import React from 'react';

interface MVPDataRow {
    achievement: string;
    points: string;
    description: string;
    isNegative?: boolean;
}

interface TableSectionProps {
    title: string;
    data: MVPDataRow[];
}

const TableSection: React.FC<TableSectionProps> = ({ title, data }) => (
    <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4 sm:p-8">
        <h2 className="text-xl font-semibold text-slate-900 md:mb-6">{title}</h2>
        <div className="overflow-x-auto">
            <table className="w-full">
                <thead>
                    <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Achievement</th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-slate-600">Points</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Description</th>
                    </tr>
                </thead>
                <tbody>
                    {data.map((row: MVPDataRow, index: number) => (
                        <tr key={index} className="border-b border-gray-100">
                            <td className="py-3.5 px-4 text-slate-900">{row.achievement}</td>
                            <td className="py-3.5 px-4 text-center">
                                <span className={`inline-block min-w-[50px] font-semibold ${row.isNegative ? 'text-red-600' : 'text-slate-900'}`}>
                                    {row.points}
                                </span>
                            </td>
                            <td className="py-3.5 px-4 text-slate-600 text-sm">{row.description}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    </div>
);

export default function MVPCalculation(): React.ReactElement {

    // ── BATTING ───────────────────────────────────────────────────────────────
    // Basic MVP = Runs ÷ 10  (10 runs = 1 MVP Point)
    // SR Bonus  = (PlayerSR ÷ TeamSR) × 1 × SR% × BasicMVP  [only if PlayerSR ≥ TeamSR]
    // SR Penalty removed | Par Score Bonus removed (latest updates)
    const battingData: MVPDataRow[] = [
        {
            achievement: 'Base Runs',
            points: 'Runs ÷ 10',
            description: '10 runs = 1 MVP Point. e.g. 60 runs → 6.0 MVP Points',
        },
        {
            achievement: 'SR Bonus — T20 (0–20 overs)',
            points: '8%',
            description: 'If PlayerSR ≥ TeamSR: (PlayerSR ÷ TeamSR) × 8% × Basic MVP added as bonus',
        },
        {
            achievement: 'SR Bonus — 21–35 overs',
            points: '6%',
            description: 'If PlayerSR ≥ TeamSR: (PlayerSR ÷ TeamSR) × 6% × Basic MVP',
        },
        {
            achievement: 'SR Bonus — 35–50 overs',
            points: '4%',
            description: 'If PlayerSR ≥ TeamSR: (PlayerSR ÷ TeamSR) × 4% × Basic MVP',
        },
        {
            achievement: 'SR Bonus — 51+ overs / Test',
            points: '2%',
            description: 'If PlayerSR ≥ TeamSR: (PlayerSR ÷ TeamSR) × 2% × Basic MVP',
        },
        {
            achievement: 'SR Penalty',
            points: '0',
            description: 'Removed — batters are no longer penalised for scoring below team SR',
        },
        {
            achievement: 'Par Score Bonus',
            points: 'Removed',
            description: 'Was: (Runs − Par Score) × match% — removed to simplify the calculation',
        },
    ];

    // ── PAR SCORE BY POSITION ─────────────────────────────────────────────────
    // Par Score = Position% × Team Total
    // Top-order expected to score more; lower-order given smaller targets
    const parScoreData: MVPDataRow[] = [
        { achievement: 'Position 1 (Opener)', points: '14%', description: 'Par Score = 14% × Team Total' },
        { achievement: 'Position 2', points: '13%', description: 'Par Score = 13% × Team Total' },
        { achievement: 'Position 3', points: '13%', description: 'Par Score = 13% × Team Total' },
        { achievement: 'Position 4', points: '12%', description: 'Par Score = 12% × Team Total' },
        { achievement: 'Position 5', points: '11%', description: 'Par Score = 11% × Team Total' },
        { achievement: 'Position 6', points: '9%', description: 'Par Score = 9% × Team Total' },
        { achievement: 'Position 7', points: '7%', description: 'Par Score = 7% × Team Total' },
        { achievement: 'Position 8', points: '6%', description: 'Par Score = 6% × Team Total' },
        { achievement: 'Position 9', points: '4%', description: 'Par Score = 4% × Team Total' },
        { achievement: 'Position 10', points: '3%', description: 'Par Score = 3% × Team Total' },
        { achievement: 'Position 11', points: '2%', description: 'Par Score = 2% × Team Total' },
    ];

    // ── BOWLING — WICKET BASE ─────────────────────────────────────────────────
    // Wicket MVP = Base Runs Per Wicket × Position Weight ÷ 10
    // Position weights: Top 4 (1–4) = 100% | Middle (5–8) = 80% | Lower (9–11) = 60%
    // e.g. T20 (17–20 ov), opener out → 18 × 100% ÷ 10 = 1.8 pts
    //      T20 (17–20 ov), #9 out     → 18 × 60%  ÷ 10 = 1.08 pts
    const bowlingWicketData: MVPDataRow[] = [
        {
            achievement: 'Wicket — 0–7 overs match',
            points: '1.2 / 0.96 / 0.72',
            description: 'Base 12 runs/wkt. Top-4: 1.2 pt | Mid (5–8): 0.96 pt | Lower (9–11): 0.72 pt',
        },
        {
            achievement: 'Wicket — 8–12 overs',
            points: '1.4 / 1.12 / 0.84',
            description: 'Base 14 runs/wkt. Top-4: 1.4 pt | Mid: 1.12 pt | Lower: 0.84 pt',
        },
        {
            achievement: 'Wicket — 13–16 overs',
            points: '1.6 / 1.28 / 0.96',
            description: 'Base 16 runs/wkt. Top-4: 1.6 pt | Mid: 1.28 pt | Lower: 0.96 pt',
        },
        {
            achievement: 'Wicket — 17–20 overs (T20)',
            points: '1.8 / 1.44 / 1.08',
            description: 'Base 18 runs/wkt. Top-4: 1.8 pt | Mid: 1.44 pt | Lower: 1.08 pt',
        },
        {
            achievement: 'Wicket — 21–26 overs',
            points: '2.0 / 1.6 / 1.2',
            description: 'Base 20 runs/wkt. Top-4: 2.0 pt | Mid: 1.6 pt | Lower: 1.2 pt',
        },
        {
            achievement: 'Wicket — 27–40 overs',
            points: '2.2 / 1.76 / 1.32',
            description: 'Base 22 runs/wkt. Top-4: 2.2 pt | Mid: 1.76 pt | Lower: 1.32 pt',
        },
        {
            achievement: 'Wicket — 41–50 overs',
            points: '2.5 / 2.0 / 1.5',
            description: 'Base 25 runs/wkt. Top-4: 2.5 pt | Mid: 2.0 pt | Lower: 1.5 pt',
        },
        {
            achievement: 'Wicket — 51–99 overs',
            points: '2.7 / 2.16 / 1.62',
            description: 'Base 27 runs/wkt. Top-4: 2.7 pt | Mid: 2.16 pt | Lower: 1.62 pt',
        },
        {
            achievement: 'Wicket — Test Match',
            points: '2.5 / 2.0 / 1.5',
            description: 'Base 25 runs/wkt. Top-4: 2.5 pt | Mid: 2.0 pt | Lower: 1.5 pt',
        },
    ];

    // ── BOWLING — BONUSES & SR ────────────────────────────────────────────────
    const bowlingBonusData: MVPDataRow[] = [
        {
            achievement: '3 Wickets in an innings',
            points: '+0.5 pt',
            description: '5 bonus runs = 0.5 MVP Point for taking 3 wickets',
        },
        {
            achievement: '5 Wickets in an innings',
            points: '+1.0 pt',
            description: '10 bonus runs = 1.0 MVP Point for taking 5 wickets',
        },
        {
            achievement: '10 Wickets in a match',
            points: '+1.5 pt',
            description: '15 bonus runs = 1.5 MVP Points for taking 10 wickets',
        },
        {
            achievement: 'Unassisted Wicket (Bowled / LBW / Hit Wicket / Mankad)',
            points: '100%',
            description: 'Bowler receives full wicket base MVP points',
        },
        {
            achievement: 'Assisted Wicket — Bowler (Catch / Stumped)',
            points: '100%',
            description: 'Bowler retains full wicket MVP; fielder earns an additional 20% on top',
        },
        {
            achievement: 'Assisted Wicket — Fielder (Catch / Stumped)',
            points: '+20%',
            description: '20% of that wicket\'s base MVP is awarded to the fielder as a bonus',
        },
        {
            achievement: 'Bowling SR Bonus — T20 (0–20 overs)',
            points: '8%',
            description: 'If TeamSR ≥ PlayerSR: (TeamSR ÷ PlayerSR) × 8% applied as SR bonus',
        },
        {
            achievement: 'Bowling SR Bonus — 21–35 overs',
            points: '6%',
            description: 'If TeamSR ≥ PlayerSR: (TeamSR ÷ PlayerSR) × 6%',
        },
        {
            achievement: 'Bowling SR Bonus — 35–50 overs',
            points: '4%',
            description: 'If TeamSR ≥ PlayerSR: (TeamSR ÷ PlayerSR) × 4%',
        },
        {
            achievement: 'Bowling SR Bonus — 51+ overs / Test',
            points: '2%',
            description: 'If TeamSR ≥ PlayerSR: (TeamSR ÷ PlayerSR) × 2%',
        },
        {
            achievement: 'Bowling SR Penalty',
            points: '0',
            description: 'Removed — bowlers are no longer penalised for conceding above team SR',
        },
        {
            achievement: 'Maiden Overs — 0–7 overs match',
            points: '1 = 1 wkt',
            description: '1 maiden over equals the value of 1 wicket',
        },
        {
            achievement: 'Maiden Overs — 8–26 overs match',
            points: '2 = 1 wkt',
            description: '2 maiden overs equal the value of 1 wicket',
        },
        {
            achievement: 'Maiden Overs — 27–50 overs',
            points: '3 = 1 wkt',
            description: '3 maiden overs equal the value of 1 wicket',
        },
        {
            achievement: 'Maiden Overs — 51+ overs / Test',
            points: '6 = 1 wkt',
            description: '6 maiden overs equal the value of 1 wicket',
        },
    ];

    // ── FIELDING ──────────────────────────────────────────────────────────────
    // Assisted (Catch/Stumped): fielder gets 20% of wicket base MVP (bowler keeps 100%)
    // Unassisted (Direct Run Out): fielder gets full wicket base MVP
    const fieldingData: MVPDataRow[] = [
        {
            achievement: 'Catch (Assisted wicket)',
            points: '+20%',
            description: '20% of the dismissed batter\'s wicket base MVP awarded to the fielder',
        },
        {
            achievement: 'Stumping (Assisted wicket)',
            points: '+20%',
            description: '20% of the dismissed batter\'s wicket base MVP awarded to the wicketkeeper',
        },
        {
            achievement: 'Run Out — Direct Hit (Unassisted)',
            points: '100%',
            description: 'Full wicket base MVP for the dismissed batter\'s position',
        },
        {
            achievement: 'Run Out — Assist',
            points: 'Shared',
            description: 'Wicket MVP points split between all fielders involved in the run out',
        },
    ];

    // ── PLAYER OF THE MATCH ───────────────────────────────────────────────────
    const potmData: MVPDataRow[] = [
        {
            achievement: 'Winning Team Player in Top 3 MVP',
            points: '✓ POTM',
            description: 'If any player from the winning team is in the Top 3 MVP list, they are declared Player of the Match',
        },
        {
            achievement: 'No Winning Team Player in Top 3',
            points: '✓ POTM',
            description: 'The overall highest MVP scorer wins, even if they are from the losing side',
        },
    ];

    return (
        <div className="min-h-screen py-6 px-4">
            <div className="max-w-6xl mx-auto">
                <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4 sm:p-8 mb-6">
                    <h1 className="text-slate-900 text-3xl font-semibold mb-3">MVP Calculation</h1>
                    <p className="text-slate-600 leading-relaxed">
                        The Most Valuable Player (MVP) system rewards players fairly for their contributions across batting, bowling, and fielding.
                        The core unit is <strong>10 runs = 1 MVP Point</strong>. Points scale by match type, batting position, and strike rate
                        relative to the team, with bonuses for key milestones. Winning team players get precedence when deciding Player of the Match.
                    </p>
                </div>

                <div className="space-y-6">
                    <TableSection title="Batting Points" data={battingData} />
                    <TableSection title="Par Score by Batting Position" data={parScoreData} />
                    <TableSection title="Bowling Points — Wicket Base" data={bowlingWicketData} />
                    <TableSection title="Bowling Points — Bonuses & Strike Rate" data={bowlingBonusData} />
                    <TableSection title="Fielding Points" data={fieldingData} />
                    <TableSection title="Player of the Match" data={potmData} />
                </div>
            </div>
        </div>
    );
}